const GRAMS_PER_DECIGRAM = 0.1;

// Absorbs binary floating-point representation error (e.g. `1.15` is stored
// as `1.1499999999999999...`, `100.05 / 0.1` lands on `1000.4999999999999`)
// so an exact half-decigram input resolves to the documented tie rule
// instead of silently falling on the wrong side because of float noise. Tiny
// relative to realistic HACCP weights (grams to low kilograms), so it never
// changes the outcome of a genuine non-tie rounding.
const FLOATING_POINT_EPSILON = 1e-9;

/**
 * Rounds a weight in grams to the nearest tenth of a gram (decigram).
 *
 * Ties (exact half-decigram values, e.g. 1.15 g) round **away from zero**
 * (commercial rounding: 1.15 -> 1.2, -1.15 -> -1.2), symmetric for negative
 * inputs. This differs from `Math.round`, which rounds negative ties toward
 * +Infinity (`Math.round(-0.5) === -0`). Away-from-zero is chosen because it
 * matches the rounding non-technical kitchen staff expect from a scale, and
 * because a stable, spec-documented tie rule is what makes the art. 18 §1
 * descending weight-order comparison and the confirmation payload hash
 * deterministic regardless of which side of the tie floating point noise
 * would otherwise land on.
 *
 * `NaN` and `Infinity` are not weights and cannot be quantized: they throw
 * rather than silently propagating a garbage value into a HACCP record.
 * Negative numbers and zero are valid inputs and are quantized like any
 * other value; whether a negative weight is meaningful is a decision for the
 * caller, not this pure function.
 */
export function quantizeWeightG(grams: number): number {
  if (!Number.isFinite(grams)) {
    throw new RangeError(`quantizeWeightG: expected a finite number of grams, received ${grams}`);
  }

  const decigrams = grams / GRAMS_PER_DECIGRAM;
  const roundedDecigrams = roundHalfAwayFromZero(decigrams);
  const quantizedGrams = roundedDecigrams / 10;

  // Normalizes -0 to 0: Object.is(-0, 0) is false, so a negative input that
  // quantizes to zero (e.g. -0.03) would otherwise compare unequal to 0 in
  // strict test assertions and serialize inconsistently downstream.
  return quantizedGrams === 0 ? 0 : quantizedGrams;
}

function roundHalfAwayFromZero(value: number): number {
  const magnitude = Math.abs(value) + FLOATING_POINT_EPSILON;
  const roundedMagnitude = Math.floor(magnitude + 0.5);
  return Math.sign(value) * roundedMagnitude;
}
